import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Template, TemplateProcessStatus } from '@workspaces/models/templates';
import { MatDivider } from '@angular/material/divider';
import { MatChipSet, MatChip } from '@angular/material/chips';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  EMPTY,
  finalize,
  fromEvent,
  map,
  merge,
  Observable,
  Subscription,
  switchMap,
  timer
} from 'rxjs';
import { WorkspaceTemplateService } from '@workspaces/services/templates.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DocumentStatusColorPipe } from '@workspaces/utils/pipes/document-status-color.pipe';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { personaFeature, PersonaFetch } from '@workspaces/store/persona.feature';
import { AgentStatusPipe } from '../../utils/pipes/agent-status.pipe';
import { AGENT_STATUS } from '@workspaces/models/agent';

@Component({
  selector: 'app-template-details-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDivider,
    MatChipSet,
    MatChip,
    DocumentStatusColorPipe,
    AgentStatusPipe
  ],
  templateUrl: './template-details-modal.component.html',
  styleUrls: ['./template-details-modal.component.scss', '../../global-workspace-styles.scss']
})
export class TemplateDetailsModalComponent implements OnInit, OnDestroy {
  public agentStatus!: AGENT_STATUS;
  public personas$!: Observable<string[]>;
  public isTriggering = false;
  public readonly TemplateProcessStatus = TemplateProcessStatus;
  private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);
  private readonly sub = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: Template,
    private dialogRef: MatDialogRef<TemplateDetailsModalComponent>,
    private readonly templateService: WorkspaceTemplateService,
    private readonly snackBar: MatSnackBar,
    private readonly store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.store.dispatch(PersonaFetch({ includeHidden: true }));
    this.sub.add(
      merge(timer(0, 60000), fromEvent(window, 'focus'), this.refreshTrigger$)
        .pipe(
          debounceTime(300),
          switchMap(() => this.templateService.getAgentRunStatus(this.data.id, this.data.version))
        )
        .subscribe(response => {
          console.log(response);
          this.agentStatus = response.status as AGENT_STATUS;
        })
    );

    this.personas$ = this.store.select(personaFeature.selectData).pipe(
      map(personas => {
        return this.data.default_personas
          .map(id => personas.find(p => p.id === id)?.name)
          .filter((name): name is string => !!name);
      })
    );
  }

  triggerAgent(): void {
    this.isTriggering = true;
    this.sub.add(
      this.templateService
        .triggerTemplateAgent(this.data.id, this.data.version)
        .pipe(
          finalize(() => (this.isTriggering = false)),
          catchError(() => {
            this.snackBar.open('Failed to trigger agent', 'Close', { duration: 3000, panelClass: ['error-snackbar'] });
            return EMPTY;
          })
        )
        .subscribe(() => {
          this.snackBar.open('Agent triggered successfully', 'Dismiss', { duration: 3000 });
          this.refreshTrigger$.next();
        })
    );
  }

  onClose(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
